import axios from "axios";
import Cookies from "js-cookie";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Eye } from "lucide-react";

import {
  API_SQE_WFH_EXCEEDED_DECISION,
  API_SQE_WFH_EXCEEDED_PENDING,
} from "../../../config/Endpoints";
import { Event } from "../../../config/events";
import { dispatchEvent } from "../../../Hooks/useEvent";
import { displayFormatDate } from "../../../utils/helpers";
import { getApi } from "../../../v2/utils/api.ts";
import { getApiErrorMessage } from "../../../v2/utils/api-error-message.ts";
import { showError, showSuccess } from "../../../v2/utils/toast.ts";
import { useSqeEmployeeNameOptions } from "../../../v2/hooks/use-sqe-employee-name-options.ts";
import PageShell from "../../../v2/components/shared/page-shell/page-shell.tsx";
import { Card } from "../../../v2/components/shared/card/card.tsx";
import { EmptyStateMessage } from "../../../v2/components/shared/empty-state-message/empty-state-message.tsx";
import { Badge, Button, Modal, ResponsiveDataTable, Select, Spinner, TextArea, Toggle } from "../../../v2/components/ui/index.ts";
import styles from "./leave-approval-view.module.css";
import { WFH_DEDUCTION_OPTIONS } from "../config/wfh-deduction-options";
import { formatLeaveTypeLabel } from "../utils/leave-type-label";

function toRequest(row) {
  return {
    ...row,
    requestId: row.requestId ?? row.wfhRequestId ?? row.leaveRequestId ?? row.id,
    employeeName: row.employeeName ?? row.empName ?? row.name ?? row.employee?.name ?? String(row.employeeId ?? "-"),
    fromDate: row.fromDate ?? row.wfhDate ?? row.requestedFor ?? row.date,
    toDate: row.toDate ?? row.fromDate ?? row.wfhDate ?? row.requestedFor ?? row.date,
    managerName: row.managerName ?? (row.managerId != null ? `ID ${row.managerId}` : "-"),
    leaveType: "WFH",
    totalDays: row.requestedDays ?? row.totalDays,
    status: row.status ?? "PENDING",
    reason: row.reason ?? row.comment ?? "",
  };
}

function getPage(payload) {
  const data = payload?.data ?? payload;
  const page = data?.data ?? data;
  const content = Array.isArray(page) ? page : page?.content ?? page?.items ?? [];
  const rows = Array.isArray(content) ? content.map(toRequest) : [];
  return { rows, totalItems: page?.totalElements ?? page?.totalItems ?? rows.length };
}

function formatRequestDate(value) {
  return value ? displayFormatDate(value) : "-";
}

const ACTION_OPTIONS = [
  { value: "APPROVED", label: "Approve" },
  { value: "REJECTED", label: "Reject" },
];
const STATUS_OPTIONS = [
  { value: "PENDING", label: "Pending" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
];
const STATUS_BADGES = {
  PENDING: { label: "Pending", variant: "warning" },
  APPROVED: { label: "Approved", variant: "success" },
  REJECTED: { label: "Rejected", variant: "danger" },
};

export default function SqeWfhExceededApproval({ viewToggle }) {
  const accessToken = Cookies.get("accessToken");
  const [rows, setRows] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [employeeId, setEmployeeId] = useState("");
  const [status, setStatus] = useState("PENDING");
  const employeeNames = useSqeEmployeeNameOptions();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [action, setAction] = useState("");
  const [deduction, setDeduction] = useState("");
  const [comment, setComment] = useState("");
  const [errors, setErrors] = useState({});

  const fetchRequests = useCallback(async (signal) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page - 1), size: String(pageSize) });
      if (employeeId) params.set("employeeId", employeeId);
      if (status) params.set("status", status);
      const url = `${API_SQE_WFH_EXCEEDED_PENDING}?${params.toString()}`;
      const response = await getApi(url, accessToken, "json", signal);
      if (signal?.aborted) return;
      const result = getPage(response.data);
      setRows(result.rows);
      setTotalItems(result.totalItems);
    } catch (error) {
      if (signal?.aborted) return;
      setRows([]);
      setTotalItems(0);
      showError(getApiErrorMessage(error, "Unable to load exceeded WFH requests."));
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [accessToken, employeeId, page, pageSize, status]);

  useEffect(() => {
    const controller = new AbortController();
    void fetchRequests(controller.signal);
    return () => controller.abort();
  }, [fetchRequests]);

  const closeModal = () => {
    if (submitting) return;
    setSelectedRequest(null);
    setAction("");
    setDeduction("");
    setComment("");
    setErrors({});
  };

  const submitDecision = async (event) => {
    event.preventDefault();
    const nextErrors = {
      ...(!action ? { action: "Select Approve or Reject." } : {}),
      ...(!deduction ? { deduction: "Select Half Day or Full Day." } : {}),
      ...(!comment.trim() ? { comment: "Comment is required." } : {}),
    };
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length || selectedRequest?.requestId == null) return;

    setSubmitting(true);
    try {
      const response = await axios.post(
        API_SQE_WFH_EXCEEDED_DECISION(selectedRequest.requestId),
        { status: action, keepDeduction: deduction === "HALF_DAY", comment: comment.trim() },
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      showSuccess(response.data?.message ?? "WFH decision saved successfully.");
      setSelectedRequest(null);
      setAction("");
      setDeduction("");
      setComment("");
      setErrors({});
      await fetchRequests();
      dispatchEvent(Event.SIDEBAR.NOTIFY_EVENT);
    } catch (error) {
      showError(getApiErrorMessage(error, "Unable to save the WFH decision."));
    } finally {
      setSubmitting(false);
    }
  };

  const columns = useMemo(() => [
    { id: "employeeName", header: "Employee Name", accessor: "employeeName", sortable: true },
    { id: "fromDate", header: "From", render: (row) => formatRequestDate(row.fromDate), getSortValue: (row) => row.fromDate ?? "", sortable: true },
    { id: "toDate", header: "To", render: (row) => formatRequestDate(row.toDate), getSortValue: (row) => row.toDate ?? "", sortable: true },
    { id: "leaveType", header: "Leave Type", render: (row) => formatLeaveTypeLabel(row.leaveType), sortable: true },
    { id: "totalDays", header: "Leave Count", accessor: "totalDays", sortable: true },
    { id: "reason", header: "Reason", render: (row) => row.reason || "-", truncate: true, getTitleText: (row) => row.reason || "-" },
    { id: "status", header: "Status", render: (row) => {
      const badge = STATUS_BADGES[row.status];
      return <Badge variant={badge?.variant ?? "neutral"}>{badge?.label ?? row.status ?? "-"}</Badge>;
    } },
    { id: "action", header: "Action", render: (row) => row.requestId != null && row.status === "PENDING" ? (
      <Button size="sm" variant="accent-ghost" startIcon={<Eye size={16} aria-hidden />} onClick={() => setSelectedRequest(row)}>View</Button>
    ) : <span className={styles.muted}>-</span> },
  ], []);
  const detailColumns = useMemo(() => [
    ...columns.filter((column) => column.id !== "action"),
    { id: "exceededDays", header: "Exceeded Days", render: (row) => row.exceededDays ?? "-" },
    { id: "deductedDays", header: "Deducted Days", render: (row) => row.deductedDays ?? "-" },
  ], [columns]);

  return <PageShell className={styles.page}>
    <Card className={styles.filterCard}>
      <div className={styles.filters}>
        <Select id="sqe-wfh-employee-filter" label="Employee Name" value={employeeId} options={employeeNames.options} placeholder={employeeNames.loading ? "Loading Employees..." : "Select Employee Name"} onChange={(value) => { setEmployeeId(value ?? ""); setPage(1); }} disabled={employeeNames.loading} searchable isClearable />
        <Select id="sqe-wfh-status-filter" label="Status" value={status} options={STATUS_OPTIONS} onChange={(value) => { setStatus(value ?? ""); setPage(1); }} isClearable />
      </div>
      <div className={styles.viewToggle}><Toggle checked={viewToggle.active} onCheckedChange={viewToggle.onCheckedChange} label="Exceeded WFH Requests" showStatusText={false} /></div>
    </Card>

    {loading ? <Card><div className={styles.loading}><Spinner /></div></Card> : rows.length ? (
      <ResponsiveDataTable aria-label="Exceeded WFH requests" columns={columns} data={rows} pagination page={page} pageSize={pageSize} totalItems={totalItems} onPageChange={setPage} onPageSizeChange={(size) => { setPageSize(size); setPage(1); }} pageSizeOptions={[5, 10, 20, 30, 40]} totalLabel={`Total Requests: ${totalItems}`} />
    ) : <Card><EmptyStateMessage message="No requests found." /></Card>}

    <Modal open={selectedRequest != null} onClose={closeModal} title="Exceeded WFH Request" size="lg" footer={<div className={styles.footer}>
      <Button type="button" variant="light-grey" disabled={submitting} onClick={closeModal}>Cancel</Button>
      <Button type="submit" form="sqe-wfh-exceeded-decision" disabled={submitting}>{submitting ? "Submitting..." : "Submit"}</Button>
    </div>}>
      {selectedRequest ? <form id="sqe-wfh-exceeded-decision" onSubmit={submitDecision} className={styles.modalForm}>
        <ResponsiveDataTable aria-label="Selected exceeded WFH request" columns={detailColumns} data={[selectedRequest]} pagination={false} />
        <div className={styles.formFields}>
          <Select id="sqe-wfh-action" label="Action" value={action} options={ACTION_OPTIONS} placeholder="Select Action" onChange={(value) => { setAction(value); setErrors((current) => ({ ...current, action: "" })); }} error={errors.action} required />
          <Select id="sqe-wfh-deduction" label="Leave Deduction" value={deduction} options={WFH_DEDUCTION_OPTIONS} placeholder="Select Leave Deduction" onChange={(value) => { setDeduction(value); setErrors((current) => ({ ...current, deduction: "" })); }} error={errors.deduction} required />
          <TextArea id="sqe-wfh-comment" label="Comment" value={comment} onChange={(event) => { setComment(event.target.value); setErrors((current) => ({ ...current, comment: "" })); }} error={errors.comment} rows={3} required />
        </div>
      </form> : null}
    </Modal>
  </PageShell>;
}
